import { getLogger } from "@expert/logging";

import type { GetDecryptFn } from "./messageProcessor";

const logger = getLogger({ module: "contactAttributeProcessor" });

/** Encryption data extracted from a contact attribute value */
interface AttributeEncryptionData {
    encryptedData: string;
    encryptedDataKey: string;
    IV: string;
}

/** Parses encryption data from a contact attribute value (JSON string) */
export function parseAttributeEncryption(attributeValue: string): AttributeEncryptionData | undefined {
    if (!attributeValue.startsWith("{")) return undefined;

    try {
        const parsed = JSON.parse(attributeValue);
        if (!parsed.encryptedData || !parsed.encryptedDataKey) return undefined;
        return {
            encryptedData: parsed.encryptedData,
            encryptedDataKey: parsed.encryptedDataKey,
            IV: parsed.IV,
        };
    } catch {
        return undefined;
    }
}

/**
 * Decrypts a single contact attribute value.
 * @returns Decrypted value, or original value if not encrypted, or "encrypted" on error
 */
export async function decryptContactAttribute(
    getDecrypt: GetDecryptFn,
    attributeValue: string,
    encryptionContext: string,
    partner: string,
    attributeKey: string,
    contactId: string,
): Promise<{ value: string; error?: boolean }> {
    const encryptionData = parseAttributeEncryption(attributeValue);

    // Not encrypted, return plain value
    if (!encryptionData) {
        return { value: attributeValue };
    }

    const { encryptedData, encryptedDataKey, IV } = encryptionData;

    try {
        const decrypt = await getDecrypt({ chatId: encryptionContext, partner }, encryptedDataKey);
        const decrypted = await decrypt({ encryptedData, IV });
        return { value: decrypted };
    } catch (error) {
        logger.error({ error, contactId, attributeKey }, "Failed to decrypt contact attribute");
        // Metric is emitted once per sync by the caller (syncContactAttributes), not per attribute,
        // to avoid inflating counts when several attributes fail on the same bad key.
        return { value: "encrypted", error: true };
    }
}
